import moment from 'moment-timezone-all'
import { createSelector } from 'reselect'
import createCachedSelector from 're-reselect'

import { selectAccountPreferredReportsAverage } from 'selectors/app/base'
import { selectCurrentPath } from 'selectors/location'
import {
  selectIsReportingOpensearchOnly,
  selectPrefersExcludeImmediatelyClosedConversationsInReports,
} from 'selectors/app/selectAccountPreferences'
import { selectById as selectKbsById } from 'subapps/kb/selectors/knowledge_bases'
import { any, compact } from 'util/arrays'
import { relativeToAbsoluteDate } from 'util/date'
import { createBasicSelector } from 'util/redux'
import { isDefined } from 'util/nullOrUndefinedChecks'
import { selectAccountTimezone } from 'ducks/currentUser/selectors/preferences/selectAccountTimezone'
import { excludeImmediatelyClosedConversations } from '../gauges/queries/filters'
import { OPENSEARCH_ONLY_MAX_LOOKBACK_DAYS } from '../constants'
import { selectBusinessHoursTimezone } from '../businessHours'

function selectBase(state) {
  return state.reports.controls
}

export function selectActiveKnowledgeBaseId(state) {
  return state.location.query && state.location.query.knowledge_base_id
}

const selectActiveKnowledgeBaseFilter = createSelector(
  selectActiveKnowledgeBaseId,
  knowledgeBaseId => {
    if (knowledgeBaseId) {
      return {
        property_name: 'kb.id',
        operator: 'eq',
        property_value: knowledgeBaseId,
      }
    }
    return null
  }
)

export function selectActiveMailboxId(state) {
  return state.location.query && state.location.query.inbox_id
}

const selectActiveMailboxFilter = createSelector(
  selectActiveMailboxId,
  mailboxId => {
    if (mailboxId) {
      return {
        property_name: 'conversation.inbox.id',
        operator: 'eq',
        property_value: mailboxId,
      }
    }
    return null
  }
)

// https://trello.com/c/w6w20mOG/3198-add-feature-flag-to-exclude-immediately-closed-conversations-from-reports
// Conversations created by the compose conversation feature are created as closed. This inserts a 0 into the business hours data in keen.
// We exclude them from reports by ignoring conversations that are open for 0 seconds.
const selectExcludeImmediatelyClosedConversationsFilter = createSelector(
  selectPrefersExcludeImmediatelyClosedConversationsInReports,
  selectCurrentPath,
  (prefersExcludeImmediatelyClosedConversationsInReports, currentPath) => {
    const isInInboxReporting = currentPath.includes('/reports/inbox')
    if (
      prefersExcludeImmediatelyClosedConversationsInReports &&
      isInInboxReporting
    ) {
      return excludeImmediatelyClosedConversations
    }
    return null
  }
)

export function selectAreBusinessHoursEnabled(state) {
  const { areBusinessHoursEnabled = false } = selectBase(state)
  return !!areBusinessHoursEnabled
}

export function selectShowBusinessHoursNotice(state) {
  const { showBusinessHoursNotice = true } = selectBase(state)
  return showBusinessHoursNotice
}

export function selectAreControlsPending(state) {
  return !!selectFocusedInput(state)
}

export function selectFocusedInput(state) {
  return selectBase(state).focusedInput
}

const selectTimezoneWithSource = createSelector(
  selectBusinessHoursTimezone,
  selectAccountTimezone,
  (businessHoursTimezone, accountTimezone) => {
    if (isDefined(businessHoursTimezone)) {
      return {
        source: 'BUSINESS_HOURS',
        timezone: businessHoursTimezone,
      }
    }

    if (isDefined(accountTimezone)) {
      return {
        source: 'ACCOUNT',
        timezone: accountTimezone,
      }
    }

    return {
      source: 'BROWSER',
      timezone: moment.tz.guess(),
    }
  }
)

export const selectTimezoneSource = createBasicSelector(
  selectTimezoneWithSource,
  timezoneWithSource => {
    return timezoneWithSource.source
  }
)

export const selectTimezone = createBasicSelector(
  selectTimezoneWithSource,
  timezoneWithSource => {
    return timezoneWithSource.timezone
  }
)

export const selectTimezoneWithOffsetDisplay = createSelector(
  selectTimezone,
  timezone => {
    return `(GMT${moment.tz(timezone).format('ZZ')}) ${timezone}`
  }
)

export const selectTimezoneOffset = createSelector(selectTimezone, timezone => {
  return moment.tz(timezone).utcOffset() / 60
})

export function selectBrowserTimezoneOffset() {
  return new Date().getTimezoneOffset() / -60
}

const selectTimeframeRaw = createSelector(selectBase, base => base.timeframe)
const selectTimeframeRawProcessed = createSelector(selectTimeframeRaw, raw => {
  if (typeof raw === 'string') return relativeToAbsoluteDate(raw)
  return raw
})

export const selectTimeframe = createSelector(
  selectTimeframeRawProcessed,
  timeframe => {
    const { end, start } = timeframe
    return {
      end: moment(end),
      start: moment(start),
    }
  }
)

export const selectToday = createSelector(selectTimezone, timezone => {
  return moment(
    moment
      .tz(timezone)
      .startOf('day')
      .format('YYYY-MM-DD')
  )
})

// Current date in the report timezone. Not memoized (unlike selectToday) so
// the OpenSearch-only boundary moves forward after midnight in an open tab.
function selectTodayDateInReportsTimezone(state) {
  return moment.tz(selectTimezone(state)).format('YYYY-MM-DD')
}

// Earliest selectable report date ('YYYY-MM-DD', report timezone) for
// OpenSearch-only accounts; null when the range is unrestricted.
export const selectReportsMinStartDate = createSelector(
  selectIsReportingOpensearchOnly,
  selectTodayDateInReportsTimezone,
  (isOpensearchOnly, todayDate) => {
    if (!isOpensearchOnly) return null
    return moment(todayDate)
      .subtract(OPENSEARCH_ONLY_MAX_LOOKBACK_DAYS, 'days')
      .format('YYYY-MM-DD')
  }
)

// Clamps a { start, end } 'YYYY-MM-DD' timeframe so it never starts before
// minStart. Ranges entirely before minStart fall back to the last 7 days.
export function clampTimeframeToMinStart(timeframe, minStart, today) {
  const { end, start } = timeframe
  if (!minStart) return timeframe
  if (moment(start).format('YYYY-MM-DD') >= minStart) return timeframe
  if (moment(end).format('YYYY-MM-DD') < minStart) {
    return relativeToAbsoluteDate('previous_7_days', today)
  }
  return { end, start: minStart }
}

export const selectTimeframeForQuery = createSelector(
  selectTimeframeRawProcessed,
  selectTimezone,
  selectReportsMinStartDate,
  selectToday,
  (rawTimeframe, timezone, minStart, today) => {
    const timeframe = clampTimeframeToMinStart(rawTimeframe, minStart, today)
    const { end, start } = timeframe
    const momentStart = moment.tz(start, timezone).startOf('day')
    return {
      end: moment
        .tz(end, timezone)
        .add(1, 'day')
        .startOf('day')
        .format(),
      start: momentStart.format(),
    }
  }
)

// False when the previous-period comparison window (the query window shifted
// back by its own length) would start before the OpenSearch-only limit, where
// no data exists and comparisons would be misleading.
export const selectIsPreviousPeriodAvailable = createSelector(
  selectTimeframeForQuery,
  selectTimezone,
  selectReportsMinStartDate,
  ({ end, start }, timezone, minStart) => {
    if (!minStart) return true
    const mStart = moment.tz(start, timezone)
    const diff = moment.duration(moment.tz(end, timezone).diff(mStart))
    return mStart.subtract(diff).format('YYYY-MM-DD') >= minStart
  }
)

export function selectTimeframeEnd(state) {
  return selectTimeframe(state).end
}

export function selectTimeframeStart(state) {
  return selectTimeframe(state).start
}

export const selectKbRatingType = createSelector(
  selectActiveKnowledgeBaseId,
  selectKbsById,
  (kbId, byId) => {
    if (kbId && byId[kbId]) return byId[kbId].ratingsType
    return any(
      ({ ratingsType }) => ratingsType === 'emoji',
      Object.values(byId)
    )
      ? 'emoji'
      : 'upvote'
  }
)

export const selectPreviousPeriodOffset = createSelector(
  selectTimeframe,
  ({ end, start }) => {
    const mEnd = moment(end)
    return moment.duration(mEnd.diff(start)).add(1, 'day')
  }
)

const selectFiltersForQuery = createSelector(
  selectActiveKnowledgeBaseFilter,
  selectActiveMailboxFilter,
  selectExcludeImmediatelyClosedConversationsFilter,
  (
    activeKnowledgeBaseFilter,
    activeMailboxFilter,
    excludeImmediatelyClosedConversationsFilter
  ) => {
    return compact([
      activeKnowledgeBaseFilter,
      activeMailboxFilter,
      excludeImmediatelyClosedConversationsFilter,
    ])
  }
)

export const selectQueryBase = createSelector(
  selectAccountPreferredReportsAverage,
  selectAreBusinessHoursEnabled,
  selectFiltersForQuery,
  selectTimeframeForQuery,
  selectTimezone,
  (average, businessHours, filters, timeframe, timezone) => {
    return {
      average,
      businessHours,
      filters,
      timeframe,
      timezone,
    }
  }
)

export const selectQueryBaseExclFilterProperties = createCachedSelector(
  selectQueryBase,
  (_, excludingFilterProperties) => excludingFilterProperties,
  (queryBase, excludingFilterProperties) => {
    if (
      !excludingFilterProperties ||
      !Array.isArray(excludingFilterProperties)
    ) {
      return queryBase
    }

    return {
      ...queryBase,
      filters: (queryBase?.filters || []).filter(
        filter => !excludingFilterProperties.includes(filter?.propertyName)
      ),
    }
  }
)(
  (_, excludingFilterProperties) =>
    (excludingFilterProperties || []).join(',') || 'UNKNOWN'
)

export const selectScopedQueryBase = createSelector(
  (state, props) => props.id,
  (state, props) => props.propertyName,
  selectQueryBase,
  (id, propertyName, queryBase) => {
    const query = {
      ...queryBase,
      scope: { [propertyName]: id },
    }
    if (propertyName === 'conversation.assignee.id') {
      query.agentId = id
    }
    if (propertyName === 'conversation.assignee_team.id') {
      query.groupId = id
    }
    return query
  }
)
